import React from "react";
import { connect } from "react-redux";
import { withStyles } from "@material-ui/styles";
import { I18n } from "@aws-amplify/core";
import update from "immutability-helper";
import PropTypes from "prop-types";
import {
  get,
  filter,
  size,
  forEach,
  map,
  sortBy,
  includes,
  keys,
} from "lodash";
import { v4 as uuidv4 } from "uuid";

import { fetchGet, fetchPost } from "../../../api/fetch";

import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Radio from "@material-ui/core/Radio";

import CustomButton from "../../Common/CustomButton";
import CustomInput from "../../Common/CustomInput";
import Loader from "../../Common/Loader";
import Instructions from "./Instructions";

import { LANGUAGES } from "../../../constants/common";

import { dbFormat } from "../../../utils/date";
import { getAttributeValue } from "../../../utils/profile";
import {
  getVerificationStatus,
  getVerificationAction,
} from "../../../utils/eventGroups";

import colors from "../../../assets/style/colors";

const styles = (theme) => ({
  surveyForm: {
    [theme.breakpoints.down("sm")]: {
      maxHeight: "none !important",
    },
  },
});

const ProfileSurveyForm = (props) => {
  const { classes, profileId, profileName, language, lov } = props;
  const [answers, setAnswers] = React.useState({});
  const [submited, setSubmited] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [verification, setVerification] = React.useState();
  const [dates, setDates] = React.useState({
    start: dbFormat(Date.now()),
  });

  const selectedVerification = get(props, ["onlyEdit", "selectedVerification"]);

  React.useEffect(() => {
    const fetchVerification = async () => {
      try {
        let res = await fetchGet(
          `verifications/${get(selectedVerification, [
            "id",
          ])}/${get(selectedVerification, ["version"])}`
        );
        setVerification({
          ...res,
          questions: filter(res.questions, (q) => q.active),
        });
      } catch (e) {
        console.log(e);
      }
    };
    fetchVerification();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const answerQuestion = (id, answer) => {
    setAnswers(
      update(answers, {
        [id]: {
          $set: answer,
        },
      })
    );
    setDates(
      update(dates, {
        [id]: {
          $set: dbFormat(Date.now()),
        },
      })
    );
  };

  const countAnswers = (type) => {
    let count = 0;
    forEach(answers, (a) => {
      if (a === type) {
        count++;
      }
    });
    return count;
  };

  const submit = async () => {
    try {
      setSaving(true);
      let verificationEventGroup;
      // Create Verification Event Group
      if (!props.eventGroupId) {
        verificationEventGroup = {
          type_id: 14,
          profile_id: profileId,
          start: dbFormat(dates.start),
          end: dbFormat(Date.now()),
        };
      }

      let submitDate = dbFormat(Date.now());
      let attributes = [
        {
          id: 184,
          value: get(verification, ["id"]),
        },
        {
          id: 185,
          value: get(verification, ["version"]),
        },
      ];
      let jsonAnswers = [];
      forEach(answers, (v, k) => {
        let q = get(
          filter(
            get(verification, ["questions"]),
            (q) => q.sequence === parseInt(k)
          ),
          0
        );
        jsonAnswers.push({
          date: dates[k],
          question: {
            number: get(q, ["sequence"]) - 1,
            text: get(q, [`question_${language}`]),
            lang: language,
            category: {
              key: get(q, ["category_key"]),
              version: 1,
            },
          },
          answer: v === true ? "yes" : v === false ? "no" : null,
          context: {
            result: null,
            confidence: null,
            attempts: null,
            attempt_list: null,
          },
        });
      });

      let [
        actionId,
        actionString,
        actionColor,
        actionColorId,
      ] = getVerificationAction(
        get(verification, ["questions"]),
        answers,
        get(props, ["user", "profile", "tag", "threat_flag"])
      );

      let attr151 = {
        survey_id: uuidv4(),
        survey_state: {
          no: countAnswers(false),
          yes: countAnswers(true),
          other:
            size(get(verification, ["questions"])) -
            countAnswers(false) -
            countAnswers(true),
        },
        survey_instructions: {
          id: actionColorId,
          profile_id: profileId,
          completed:
            size(get(verification, ["questions"])) ===
            countAnswers(true) + countAnswers(false),
          reason: null,
          color: actionColor,
          instruction: actionString,
        },
        session: null,
        event_group: props.eventGroupId || null,
        camera_id: null,
        start: get(dates, ["start"]),
        end: submitDate,
        questions: {
          answers: jsonAnswers,
          operations: null,
        },
        profile: profileId,
        name:
          profileName ||
          `${get(props, ["user", "firstName"])} ${get(props, [
            "user",
            "lastName",
          ])}`,
        verification_event_group: null,
        survey_start_event_id: null,
        presurvey_start_event_id: null,
        edge_id: null,
        event_start: null,
      };

      attributes.push({
        id: 151,
        value: JSON.stringify(attr151),
      });

      let surveyEvent = {
        type_id: 81,
        profile_id: profileId,
        event_group_id: get(verificationEventGroup, ["id"]),
        log_level: 1,
        attributes,
      };
      if (props.eventGroupId) {
        surveyEvent.event_group_id = props.eventGroupId;
      }

      // Create Luke Action Event
      let lukeActionEvent = {
        type_id: 88,
        profile_id: profileId,
        event_group_id: get(verificationEventGroup, ["id"]),
        log_level: 1,
        attributes: [
          {
            id: 158,
            value: actionId,
          },
          {
            id: 159,
            value: JSON.stringify({
              id: actionColorId,
              profile_id: profileId,
              completed: true,
              reason: null,
              color: actionColor,
              instruction: actionString,
            }),
          },
        ],
      };

      if (!get(props, ["onlyEdit", "noVerificationSubmit"])) {
        // Submit Verification event group
        verificationEventGroup = await fetchPost(
          "event_groups",
          verificationEventGroup
        );

        // Submit survey event
        surveyEvent.event_group_id = get(verificationEventGroup, ["id"]);
        surveyEvent = await fetchPost("events", surveyEvent);

        // Submit luke action event
        lukeActionEvent.event_group_id = get(verificationEventGroup, ["id"]);
        lukeActionEvent.parent_event_id = get(surveyEvent, ["id"]);
        lukeActionEvent = await fetchPost("events", lukeActionEvent);
      }

      if (get(props, ["onlyEdit", "onSubmit"])) {
        props.onlyEdit.onSubmit(
          verificationEventGroup,
          surveyEvent,
          lukeActionEvent,
          verification
        );
      }

      setSaving(false);
      if (props.eventGroupId) {
        close();
      } else {
        setSubmited({
          verificationEventGroup,
          surveyEvent,
          lukeActionEvent,
        });
      }
      if (get(props, ["onlyEdit", "noConfirmation"])) {
        close();
      }
    } catch (e) {
      setSaving(false);
      console.log(e);
    }
  };

  const close = () => {
    setAnswers({});
    setSubmited(false);
    props.onClose();
  };

  const isValidForm = () => {
    if (!verification) return false;
    forEach(get(verification, ["questions"]), (q) => {
      if (q.mandatory && !includes(keys(answers), q.sequence)) {
        return false;
      }
    });
    return true;
  };

  if (submited) {
    return (
      <Instructions
        profileName={`${get(props, ["user", "firstName"])} ${get(props, [
          "user",
          "lastName",
        ])}`}
        surveyStatus={getVerificationStatus(get(submited, ["surveyEvent"]))}
        lukeActionId={getAttributeValue(
          get(submited, ["lukeActionEvent"]),
          158
        )}
        lov={lov}
        language={language}
      />
    );
  }

  if (saving) {
    return (
      <Grid
        item
        xs
        className={"flexCenter"}
        style={{ justifyContent: "center", minHeight: "300px" }}
      >
        <Loader noDecoration message={I18n.get("profiles_saving")} />
      </Grid>
    );
  }

  return (
    <Grid item xs>
      <Grid container direction="column">
        <Grid
          item
          xs
          style={{
            border: `1px solid ${colors.veryLighPink}`,
            borderRadius: "4px",
          }}
        >
          <Grid
            container
            direction="column"
            wrap="nowrap"
            className={classes.surveyForm}
            style={{
              minHeight: props.dialog
                ? ""
                : //? "calc(100vh - 320px)"
                  "calc(100vh - 495px)",
              maxHeight: props.dialog
                ? "calc(100vh - 230px)"
                : "calc(100vh - 495px)",
              overflow: "auto",
              padding: "12px",
            }}
          >
            {verification ? (
              map(
                sortBy(get(verification, ["questions"]), (q) => q.sequence),
                (q, index) => (
                  <Grid item key={q.sequence} style={{ margin: "5px 0px" }}>
                    <Box className="text-87 bold-500">
                      {`${index + 1}. ${q[`question_${language}`]} ${
                        q.mandatory ? "*" : ""
                      }`}
                    </Box>
                    {get(q, ["data_type", "code"]) === "boolean" ? (
                      <Grid container>
                        <Grid item>
                          <Radio
                            color="primary"
                            checked={answers[q.sequence] === false || false}
                            onChange={(e) => answerQuestion(q.sequence, false)}
                          />
                          {I18n.get("site_no")}
                        </Grid>
                        <Grid item>
                          <Radio
                            color="primary"
                            checked={answers[q.sequence] === true || false}
                            onChange={(e) => answerQuestion(q.sequence, true)}
                          />
                          {I18n.get("site_yes")}
                        </Grid>
                      </Grid>
                    ) : get(q, ["data_type", "code"]) === "float" ? (
                      <Grid container>
                        <Grid
                          item
                          style={{
                            maxWidth: "62px",
                            marginRight: "10px",
                          }}
                        >
                          <CustomInput
                            name="floatInput"
                            value={answers[q.sequence] || ""}
                            onChange={(e) => {
                              let regex = /^[0-9]*\.?[0-9]*$/;
                              if (
                                regex.test(e.target.value) ||
                                e.target.value === ""
                              ) {
                                answerQuestion(q.sequence, e.target.value);
                              }
                            }}
                          />
                        </Grid>
                        {/* {lov[88].map((item) => (
                          <Grid item key={item.id}>
                            <Box
                              onClick={() => answerQuestion(88, item.id)}
                              className="flexCenter"
                              style={{
                                border:
                                  answers[88] === item.id
                                    ? `2px solid ${colors.primary}`
                                    : "none",
                                borderRadius: "2px",
                                color: colors.primary,
                                height: "36px",
                                width: "36px",
                                justifyContent: "center",
                                marginRight: "5px",
                                fontWeight: "bold",
                                cursor: "pointer",
                              }}
                            >
                              {item[`name_${language}`]}
                            </Box>
                          </Grid>
                        ))} */}
                      </Grid>
                    ) : (
                      <></>
                    )}
                  </Grid>
                )
              )
            ) : (
              <Grid item style={{ marginTop: props.dialog ? "0px" : "30%" }}>
                <Loader noDecoration={true} height="300px" />
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item style={{ marginTop: "12px" }}>
          <Grid container justify="center">
            <Grid item style={{ margin: "0px 10px" }}>
              <CustomButton color="primary" onClick={close}>
                {I18n.get("site_cancel")}
              </CustomButton>
            </Grid>
            <Grid item style={{ margin: "0px 10px" }}>
              <CustomButton
                variant="contained"
                color="primary"
                disabled={!isValidForm()}
                onClick={submit}
              >
                {I18n.get("auth_submit")}
              </CustomButton>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

ProfileSurveyForm.propTypes = {
  survey: PropTypes.object,
  profileId: PropTypes.string,
  language: PropTypes.oneOf(LANGUAGES),
  lov: PropTypes.object,
  onClose: PropTypes.func,
  dialog: PropTypes.bool,
  onlyEdit: PropTypes.object,
  eventGroupId: PropTypes.number,
};

const mapStateToProps = (state) => {
  return {
    user: state.user,
  };
};

export default connect(mapStateToProps)(withStyles(styles)(ProfileSurveyForm));
